const minCheckIn = 1; // 1 seconds
const defaultCheckIn = 900; // Default to 15 minutes
const maxCheckIn = 86400; // 24 hours



export interface ICommand {
    readonly command: string;
    toString(): string;
}

export class UpdateThemeCommand implements ICommand {
    public command = 'update theme';
    themeId: string;

    constructor(themeId: string) {
        this.themeId = themeId;
    }

    withTheme(themeId: string) {
        return new UpdateThemeCommand(themeId);
    }

    toString() {
        return `${this.command} ${this.themeId || ''}`;
    }
}

export class UpdateConfigCommand implements ICommand {
    public command = 'update config';
    deviceConfigId: string;

    // Technically, this command takes a deviceConfigId parameter...
    // but it is supplied by the checkin lambda. I don't want users
    // to be able to specify a config ID here via the UI because
    // the ID is supposed to come from the devices table.
    constructor(deviceConfigId?: string) {
        this.deviceConfigId = deviceConfigId || '';
    }

    toString() {
        return this.deviceConfigId ? `${this.command} ${this.deviceConfigId}` : this.command;
    }
}

export class UpdateSoftwareCommand implements ICommand {
    public command = 'update software';
    filename: string;
    force: boolean;

    constructor(filename: string, force: boolean) {
        this.filename = filename;
        this.force = force;
    }

    withFilename(filename: string): UpdateSoftwareCommand {
        return new UpdateSoftwareCommand(filename, this.force);
    }

    withForce(force: boolean): UpdateSoftwareCommand {
        return new UpdateSoftwareCommand(this.filename, force);
    }

    toString() {
        return `${this.command} ${this.filename || ''}${this.force ? ' 1' : ''}`;
    }
}

export class GetLogsCommand implements ICommand {
    public command = 'get logs';

    toString() {
        return this.command;
    }
}

export class GetScreenCommand implements ICommand {
    public command = 'get screen';

    toString() {
        return this.command;
    }
}

export class GetSettingsCommand implements ICommand {
    public command = 'get settings';

    toString() {
        return this.command;
    }
}

export class RestartSystemCommand implements ICommand {
    public command = 'restart system';

    toString() {
        return this.command;
    }
}

export class RestartSoftwareCommand implements ICommand {
    public command = 'restart software';

    toString() {
        return this.command;
    }
}

export class SetCommand implements ICommand {
    public command = 'set';
    setting: string;
    value: string;

    constructor(setting: string, value: string) {
        this.setting = setting || '';
        this.value = typeof value === 'undefined' ? '' : value;
    }

    withSetting(setting: string): SetCommand {
        return new SetCommand(setting, this.value);
    }

    withValue(value: string): SetCommand {
        return new SetCommand(this.setting, value);
    }

    toString() {
        return `${this.command} ${this.setting || ''}${typeof this.value === 'undefined' ? '' : ' ' + this.value}`;
    }
}

export class CommentCommand implements ICommand {
    public command = 'comment';
    comment?: string;

    constructor(comment: string) {
        this.comment = comment || '';
    }

    toString() {
        return `#${this.comment}`;
    }
}

type Parser = {
    name: string;
    regex: RegExp;
    extract: (m: string[]) => ICommand;
    remoteChange: boolean; // Does the command correspond to a "remote update" restricted by 2022 Building Code?
};

const parsers: Parser[] = [
    {
        name: 'update theme',
        regex: /^update theme(?:\s+(?:https:\/\/.*?\/content\/)?([a-f0-9-]{36}))?/gi,
        extract: m => new UpdateThemeCommand(m[1]),
        remoteChange: false, // It is acceptable to update themes remotely
    },
    {
        name: 'update config',
        regex: /^update config(?:\s+([a-f0-9-]{36}))?/gi,
        extract: m => new UpdateConfigCommand(m[1]),
        remoteChange: true,
    },
    {
        name: 'update software',
        regex: /^update software(?:\s+([^/ ]+?)|\s+https:\/\/.*?\/uploads\/(.*?)(?:\?\S*)?\s+\S+)?(?:\s+([01]?))?$/gi,
        extract: m => new UpdateSoftwareCommand(m[1] || m[2], !!m[3]),
        remoteChange: true,
    },
    {
        name: 'get logs',
        regex: /^get logs\b/gi,
        extract: m => new GetLogsCommand(),
        remoteChange: false,
    },
    {
        name: 'get screen',
        regex: /^get screen\b/gi,
        extract: m => new GetScreenCommand(),
        remoteChange: false,
    },
    {
        name: 'get settings',
        regex: /^get settings\b/gi,
        extract: m => new GetSettingsCommand(),
        remoteChange: false,
    },
    {
        name: 'set',
        regex: /^set(?:\s+(\S+)(?:\s+(.*))?)?/gi,
        extract: m => new SetCommand(m[1], m[2]),
        remoteChange: true,
    },
    {
        name: 'restart software',
        regex: /^restart software\b/gi,
        extract: m => new RestartSoftwareCommand(),
        remoteChange: false,
    },
    {
        name: 'restart system',
        regex: /^restart system\b/gi,
        extract: m => new RestartSystemCommand(),
        remoteChange: false,
    },
    {
        name: 'comment',
        regex: /^#(.*)$/gi,
        extract: m => new CommentCommand(m[1]),
        remoteChange: false,
    },
];

export const allCommandNames = parsers.map(p => p.name);
export const allowedRemoteCommandNames = parsers.filter(p => !p.remoteChange).map(p => p.name);

export interface IDeviceScriptData {
    commands: ICommand[];
    checkin: number;
}

export class DeviceScript implements IDeviceScriptData {
    public commands: ICommand[];
    public checkin: number;

    constructor({ commands, checkin }: Partial<IDeviceScriptData>) {
        this.commands = commands || [
            new UpdateThemeCommand(''),
        ];
        this.checkin = checkin || defaultCheckIn;
    }
    
    containsCommand(command: string): boolean {
        return this.commands.some(c => c.command === command);
    }

    removeCommandAt(index: number): DeviceScript {
        return new DeviceScript({
            commands: this.commands.filter((_, i) => i !== index),
            checkin: this.checkin,
        });
    }

    moveCommandUp(index: number): DeviceScript {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index - 1),
                this.commands[index],
                this.commands[index - 1],
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    moveCommandDown(index: number): DeviceScript {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                this.commands[index + 1],
                this.commands[index],
                ...this.commands.slice(index + 2),
            ],
            checkin: this.checkin,
        });
    }

    setCheckIn(seconds: number): DeviceScript {
        return new DeviceScript({
            commands: this.commands,
            checkin: Math.max(minCheckIn, Math.min(seconds || defaultCheckIn, maxCheckIn)),
        });
    }

    addCommand(commandName: string): DeviceScript {
        return new DeviceScript({
            commands: [
                ...this.commands,
                parsers.filter(p => p.name === commandName).shift()!.extract([ '', '' ]),
            ],
            checkin: this.checkin,
        });
    }

    selectTheme(index: number, themeId: string) {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                (this.commands[index] as UpdateThemeCommand).withTheme(themeId),
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    selectSoftware(index: number, filename: string) {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                (this.commands[index] as UpdateSoftwareCommand).withFilename(filename),
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    forceSoftware(index: number, force: boolean) {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                (this.commands[index] as UpdateSoftwareCommand).withForce(force),
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    setSetting(index: number, setting: string) {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                (this.commands[index] as SetCommand).withSetting(setting),
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    setValue(index: number, value: string) {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                (this.commands[index] as SetCommand).withValue(value),
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    setComment(index: number, comment: string) {
        return new DeviceScript({
            commands: [
                ...this.commands.slice(0, index),
                new CommentCommand(comment),
                ...this.commands.slice(index + 1),
            ],
            checkin: this.checkin,
        });
    }

    toString() {
        return JSON.stringify({
            commands: this.commands.map(c => c.toString()),
            checkin: this.checkin,
        }, null, '  ');
    }
}

export const parse = (script: string | null, disableRemoteUpdates: boolean): DeviceScript => {
    const deviceScript = new DeviceScript({ commands: [], checkin: defaultCheckIn });
    if (!script) {
        return deviceScript;
    }
    let obj: any;
    try {
        obj = JSON.parse(script);
        deviceScript.checkin = obj.checkin;
        for (const i in obj.commands) {
            let matched = false;
            for (const parser of parsers) {
                parser.regex.lastIndex = 0; // Reset the regex (or could remove the /g flag)
                const match: string[] = parser.regex.exec(obj.commands[i])!;
                if (!match) continue;
                if (parser.remoteChange && disableRemoteUpdates) continue; // Convert command to a comment
                deviceScript.commands.push(
                    parser.extract(match)
                );
                matched = true;
                break;
            }
            if (!matched) {
                deviceScript.commands.push(
                    new CommentCommand(obj.commands[i])
                );
            }
        }
    }
    catch { /* swallow error */ }
    return deviceScript;
};
